import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, Plus, Trash2, X, History as HistoryIcon, Save } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import HtmlContent from './HtmlContent';
import { listDocs, getDoc, createDoc, updateDoc, deleteDoc } from '../services/api';
import { toast } from '../hooks/use-toast';

// Compact rich-text docs attached to a ticket. Auto-saves on the Save button;
// the previous version is preserved on every content change (up to 30 versions).
const DocsTab = ({ ticketId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDoc, setOpenDoc] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showVersions, setShowVersions] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listDocs(ticketId); setItems(r.data || []); }
    catch { /* silent */ } finally { setLoading(false); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (d) => {
    try { const full = await getDoc(d.id); setOpenDoc(full); }
    catch { toast({ title: 'Failed to load doc', variant: 'destructive' }); }
  };

  const create = async () => {
    try {
      const d = await createDoc({ ticket_id: ticketId, title: 'Untitled document', content_html: '' });
      setShowNew(false);
      openEdit(d);
      load();
    } catch { toast({ title: 'Create failed', variant: 'destructive' }); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try { await deleteDoc(d.id); load(); }
    catch (err) { toast({ title: 'Delete failed', variant: 'destructive', description: err?.response?.data?.detail || '' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">Documents</div>
        <button onClick={create} data-testid="new-doc-btn" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0f5c3d] text-white text-xs font-medium">
          <Plus className="w-3 h-3" /> New doc
        </button>
      </div>
      {loading ? (
        <div className="p-6 text-center text-sm text-[#0b1a2e]/50"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#0b1a2e]/50 border border-dashed border-black/10 rounded-lg">
          <FileText className="w-8 h-8 mx-auto mb-2 text-[#0b1a2e]/20" />
          No documents yet — create your first spec, minutes or SOP.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} data-testid="doc-row" className="bg-white border border-black/5 rounded-lg p-3 flex items-center gap-3 hover:bg-[#f7fbf8]">
              <FileText className="w-4 h-4 text-[#0f5c3d]" />
              <button onClick={() => openEdit(d)} className="flex-1 text-left">
                <div className="font-medium text-sm text-[#0b1a2e]">{d.title}</div>
                <div className="text-[11px] text-[#0b1a2e]/50">Updated {new Date(d.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</div>
              </button>
              <button onClick={() => remove(d)} className="p-1.5 rounded text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {openDoc && (
        <DocEditor
          doc={openDoc}
          onClose={() => { setOpenDoc(null); setShowVersions(false); load(); }}
          showVersions={showVersions}
          setShowVersions={setShowVersions}
        />
      )}
    </div>
  );
};

const DocEditor = ({ doc, onClose, showVersions, setShowVersions }) => {
  const [title, setTitle] = useState(doc.title);
  const [content, setContent] = useState(doc.content_html || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateDoc(doc.id, { title, content_html: content });
      toast({ title: 'Saved' });
      setDirty(false);
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const restore = (v) => {
    if (!window.confirm(`Restore this version? Current draft will be pushed to history.`)) return;
    setTitle(v.title || title);
    setContent(v.content_html || '');
    setDirty(true);
    setShowVersions(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-black/5 flex items-center gap-3">
          <FileText className="w-4 h-4 text-[#0f5c3d]" />
          <input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} data-testid="doc-title" className="flex-1 border-0 outline-none text-lg font-medium text-[#0b1a2e]" placeholder="Document title" />
          <button onClick={() => setShowVersions((s) => !s)} data-testid="doc-history-btn" title="Version history" className="p-1.5 rounded hover:bg-black/5"><HistoryIcon className="w-4 h-4" /></button>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 relative">
          {showVersions ? (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-3">Version history ({(doc.versions || []).length})</div>
              {(doc.versions || []).length === 0 ? (
                <div className="text-sm text-[#0b1a2e]/50">No previous versions yet — versions are captured each time you save changes.</div>
              ) : (doc.versions || []).slice().reverse().map((v, i) => (
                <div key={i} className="border-l-2 border-[#0f5c3d]/30 pl-4 py-2 mb-2 relative">
                  <div className="absolute -left-[7px] top-3 w-3 h-3 rounded-full bg-[#0f5c3d]" />
                  <div className="text-xs text-[#0b1a2e]/60">Saved by {v.saved_by_name} · {new Date(v.saved_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</div>
                  <div className="mt-2 border border-black/5 rounded-lg p-3 bg-[#f7fbf8]/50 text-sm max-h-40 overflow-y-auto">
                    <HtmlContent html={v.content_html || '<em>Empty</em>'} />
                  </div>
                  <button onClick={() => restore(v)} className="mt-1 text-xs text-[#0f5c3d] font-semibold hover:underline">Restore this version</button>
                </div>
              ))}
            </div>
          ) : (
            <RichTextEditor value={content} onChange={(v) => { setContent(v); setDirty(true); }} placeholder="Write your document…" data-testid="doc-body" />
          )}
        </div>
        <div className="px-5 py-3 border-t border-black/5 bg-[#f7fbf8] flex justify-between items-center">
          <div className="text-xs text-[#0b1a2e]/60">
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </div>
          <button onClick={save} disabled={saving || !dirty} data-testid="doc-save-btn" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocsTab;
